import React, { FC, Fragment } from 'react';
import {
    Box,
    Typography,
    Grid,
    Alert,
    AlertTitle,
    Checkbox,
    FormControl,
    FormControlLabel,
    Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'utils/format';
import { TRANSPORT_DATE_KEYS, transportDateHelper } from 'utils/transport-date-helper';
import { Order } from 'models/order';
import { TransportServices } from 'models/transport';
import TransportDatePicker from 'components/shared/transport-date-picker/transport-date-picker.component';
import TransportDeadline from 'components/shared/transport-deadline/transport-deadline.component';

type CreateDeliveryTransportSectionProps = {
    order?: Order;
    isCreating: boolean;
    onIsCreatingChange: (isCreating: boolean) => void;
    date: Date | null;
    onDateChange: (date: Date | null) => void;
    deadline: string;
    onDeadlineChange: (deadline: string) => void;
};

const CreateDeliveryTransportSection: FC<CreateDeliveryTransportSectionProps> = ({
    order,
    isCreating,
    onIsCreatingChange,
    date,
    onDateChange,
    deadline,
    onDeadlineChange,
}: CreateDeliveryTransportSectionProps) => {
    const { t: translate } = useTranslation(['common', 'transport']);

    const scheduledDeliveryDate = () => {
        if (!order?.currentDelivery?.transportDate) {
            return new Date();
        }
        const pickupFromLabDate = new Date(order.currentDelivery.transportDate);
        return order.currentDelivery.isSameDayDelivery
            ? pickupFromLabDate
            : transportDateHelper.nextWorkingDay(pickupFromLabDate, true);
    };

    const formatPickupDate = () => {
        const formattedDate = transportDateHelper.transportDateFormatter(
            transportDateHelper.nextWorkingDayAsString(date, false),
            'date',
        );
        if (TRANSPORT_DATE_KEYS.includes(formattedDate)) {
            return translate(`transport:${formattedDate}`);
        }
        return formattedDate;
    };

    return (
        <Box mt={2}>
            <Box display="flex" alignItems="center">
                <Typography variant="h4">{translate('transport:TRANSPORT')}</Typography>
            </Box>
            {order?.currentDelivery ? (
                <Box display="flex" alignItems="center">
                    <Box mr={1}>
                        <CheckCircleIcon color="success" />
                    </Box>
                    <Box>
                        <Typography variant="subtitle2">
                            {translate('transport:TRANSPORT_DELIVERY_ALREADY_SCHEDULED')}
                        </Typography>
                        <Box>{formatDate(scheduledDeliveryDate(), 'date')}</Box>
                    </Box>
                </Box>
            ) : (
                <>
                    <FormControl margin="dense" fullWidth>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={isCreating}
                                    onChange={(event) => onIsCreatingChange(event.target.checked)}
                                    data-test="sent-by-lab-dialog.create-delivery-transport-checkbox"
                                />
                            }
                            label={translate('transport:CREATE_DELIVERY_TRANSPORT')}
                        />
                    </FormControl>
                    {isCreating ? (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Fragment>
                                <Grid container spacing={2} mt={0.5}>
                                    <Grid item xs={12} sm={6}>
                                        <TransportDatePicker
                                            date={date}
                                            onDateChange={onDateChange}
                                            type={TransportServices.DELIVERY}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TransportDeadline
                                            readOnly={false}
                                            deadline={deadline}
                                            onDeadlineChange={onDeadlineChange}
                                        />
                                    </Grid>
                                </Grid>
                                <Box mt={2}>
                                    <Alert icon={false} severity="info">
                                        <AlertTitle>
                                            <Typography variant="subtitle2">
                                                {translate('transport:PICKUP_FROM_YOU_DATE')}
                                            </Typography>
                                        </AlertTitle>
                                        <Box>{formatPickupDate()}</Box>
                                    </Alert>
                                </Box>
                            </Fragment>
                        </Paper>
                    ) : undefined}
                </>
            )}
        </Box>
    );
};

export default CreateDeliveryTransportSection;
